<template>
  <page-template :is-loading="isLoading" :data-exists="namespaces !== null">
    <template #pageHeadline>
      {{ $t('systemDB.listPage.pageHeadline') }}
    </template>
    <template #pageContent>
      <rbz-table
        :columns="columns"
        :table-data="namespaces"
        :sticky-header="true"
        class="rbz-list-table"
        data-qa="namespaces-table"
        :show-download-button="false"
        :row-buttons="rowButtons"
        :new-button-link="getNewNamespacePath()"
        :show-filter-row="true"
        @delete-row-button-clicked="openDeleteNamespaceModal"
      >
      </rbz-table>
      <rbz-modal-dialog
        :show="showDeleteNamespaceModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteNamespace"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteNamespaceModal = false"
        @closeModal="showDeleteNamespaceModal = false"
        size="medium"
      >
        <template #content>
          {{ $t('common.operations.delete.modal.message', { name: selectedNamespace }) }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>

<script lang="ts">
import _ from 'lodash';
import RequestsUtils from '@/helpers/RequestsUtils';
import { defineComponent } from 'vue';
import { GenericObject } from '@/types';
import PageTemplate from '@/views/PageTemplate.vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'SystemDBList',
  setup() {
    const { getEditPath, getNewPath, navigateTo } = useRouting();
    return {
      getEditPath,
      getNewPath,
      navigateTo,
    };
  },
  components: {
    PageTemplate,
    RbzModalDialog,
    RbzTable,
  },
  data() {
    return {
      columns: [
        {
          id: 'name',
          title: this.$t('systemDB.listPage.name'),
          fieldNames: ['id'],
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          classes: 'clickable',
          autoFocus: true,
          linkCallback: (item) => {
            return this.getNamespacePath(item);
          },
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          linkCallback: this.getNamespacePath,
        },
        {
          id: 'delete',
          icon: 'IconDelete',
          disabledCallback: this.isDeleteDisabled,
          tooltipCallback: this.deleteTooltip,
        },
      ] as RbzTableRowButton[],
      namespaces: null as GenericObject[],
      isLoading: true,

      showDeleteNamespaceModal: false,
      selectedNamespace: null as string,
    };
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    entityName() {
      return {
        plural: this.$t('systemDB.plural'),
        singular: this.$t('systemDB.singular'),
      };
    },
  },
  methods: {
    getNamespacePath(namespaceObject: GenericObject) {
      return this.getEditPath('system-db', namespaceObject.id);
    },

    editNamespace(namespaceObject: GenericObject) {
      this.navigateTo(this.getNamespacePath(namespaceObject));
    },

    async loadData() {
      this.isLoading = true;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: 'db',
      });
      this.namespaces = _.map(response?.data?.items, (namespace: string) => {
        return {
          id: namespace,
        };
      });
      this.isLoading = false;
    },

    async deleteNamespace() {
      const namespace: string = this.selectedNamespace;
      this.isLoading = true;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `db/${namespace}`,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.namespaces = _.filter(this.namespaces, (namespaceItem) => {
          return namespaceItem.id !== namespace;
        });
      }
      this.showDeleteNamespaceModal = false;
      this.isLoading = false;
    },

    isDeleteDisabled(namespaceObject: GenericObject): boolean {
      return this.isNamespaceProtected(namespaceObject);
    },

    isNamespaceProtected(namespaceObject: GenericObject): boolean {
      return namespaceObject.id === 'system';
    },

    deleteTooltip(namespaceObject: GenericObject): string {
      if (this.isNamespaceProtected(namespaceObject)) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      return this.$t('common.operations.delete.tableTooltip');
    },

    openDeleteNamespaceModal(namespace: GenericObject) {
      this.showDeleteNamespaceModal = true;
      this.selectedNamespace = namespace.id;
    },

    getNewNamespacePath() {
      return this.getNewPath('system-db');
    },
  },
  created() {
    this.loadData();
  },
});
</script>
